<script setup>
import BnFooter from '@/components/BnFooter';
import BnInfo from '@/components/BnInfo.vue';
import BnLoading from '@/components/BnLoading.vue';
import BnToolbarBtn from '@/components/BnToolbarBtn.vue';
import DataProvider from '@/components/DataProvider.js';

import { $httpFhirApi } from '@/common/api/httpFhir.service.js';
import { SPECIAL_VARIABLES } from '@/common/api/specialVariables.api';
import { isLibraryDatabase } from '@/common/config';
import { addRefToBackBtnListiner, removeRefToBackBtnListiner } from '@/common/nativeBackButton';
import { useProfileFlow } from '@/common/workflowEngine/useProfileFlow';
import { HealthcareService } from '@/fhirworks';
import { debounce, isEmpty } from 'lodash';
import { useDevOnly } from '@/composables/useDevOnly';
import { ref, computed, watch } from 'vue';
import { useDisplay } from 'vuetify';

const { isProfile, isTenant } = useDevOnly();
const display = useDisplay();

defineOptions({
    name: 'BnResourceSelector',
    inheritAttrs: false,
});

// Template refs
const dpResourceRef = ref(null);
const searchRef = ref(null);
const bnDialogRef = ref(null);

// Dialog management state
const dialog = ref(false);
const resolve = ref(null);
const reject = ref(null);
const options = ref(null);
const baseOptions = {
    header: 'BestNotes',
    width: 400,
    persistent: true,
    filter: true,
    multiple: true,
    info: undefined,
    warning: undefined,
    group: {},
};
const props = ref({});
const agency = ref(undefined);
const dpResource = ref([]);

// Filter/search state
const search = ref(undefined);
const remoteSearch = ref(false);
const loadingMoreData = ref(false);

// Constants
const specialVariables = SPECIAL_VARIABLES;

// Helper to extract display from a URL (e.g., "http://example.com/Questionnaire/my-form" -> "my-form")
function getDisplay(url) {
    if (!url) return '';
    if (typeof url === 'string') {
        // Extract the last segment after the last slash, and before any | (version separator)
        const withoutVersion = url.split('|')[0];
        const segments = withoutVersion.split('/');
        return segments[segments.length - 1] || url;
    }
    return url?.display || url?.name || '';
}

// Computed
const moreDataUrl = computed(() => {
    if (loadingMoreData.value) {
        // leave this here to maintain infinite scrolling reactivity
    }
    return dpResourceRef.value?.link?.find((item) => item.relation === 'next')?.url;
});

const resources = computed(() => {
    /**
     * TODO: update search to modify the actual query so that we can search
     * more than just the first 100 rows
     */
    const filteredResources = dpResource.value.filter((item) => {
        let searchStr;
        if (!search.value) return true;
        else if (props.value.filterByAgency) {
            if (agency.value === item[props.value.filterByAgency]?.id) {
                // multi-prop search
                if (Array.isArray(props.value.itemTitle)) {
                    let found = false;
                    for (const prop of props.value.itemTitle) {
                        if (!item[prop]) continue;
                        searchStr = item[prop].toLowerCase();
                        found = found || searchStr.includes(search.value.toLowerCase());
                    }
                    return found;
                }
                // regular search
                else {
                    if (!item[props.value.itemTitle]) return false;
                    searchStr = item[props.value.itemTitle].toLowerCase();
                    return searchStr.includes(search.value.toLowerCase());
                }
            }
        } else {
            // multi-prop search
            if (Array.isArray(props.value.itemTitle)) {
                let found = false;
                for (const prop of props.value.itemTitle) {
                    if (!item[prop]) continue;
                    searchStr = item[prop].toLowerCase();
                    found = found || searchStr.includes(search.value.toLowerCase());
                }
                return found;
            }
            // regular search
            else {
                if (!item[props.value.itemTitle]) return false;
                searchStr = item[props.value.itemTitle].toLowerCase();
                return searchStr.includes(search.value.toLowerCase());
            }
        }
        return true;
    });

    // group questionnaires by subjectType
    if (props.value.preset === 'Questionnaire') {
        const subjectTypeMap = { Patient: 'Patient', Person: 'Individual', Practitioner: 'Professional', Organization: 'Business', CareTeam: 'Employee' };
        let questionnaires = filteredResources.filter((e) => e.resourceType === 'Questionnaire');
        const collection = filteredResources.filter((e) => e.resourceType === 'BN_List');

        if (props.value.group !== null) {
            questionnaires = questionnaires.filter((item) => (props.value.group ? item.groupNote : !item?.groupNote));
        }

        // if profile account do NOT show deprecated forms
        if (isProfile() || !isTenant('goffin')) {
            questionnaires = questionnaires.filter((item) => !item.deprecate);
        }

        // Filter out forms with disallowPortal=true for portal contexts
        if (props.value.isPortalPacket) {
            questionnaires = questionnaires.filter((item) => !item.disallowPortal);
        }

        return questionnaires
            .map((item) => {
                item.collectionName = collection
                    .filter((c) => c.item.find((i) => item.url === i.value.uri))
                    .sort((a, b) => (a > b ? 1 : -1))
                    .map((e) => e.name)
                    .join(', ');
                item.subTitle = item.subjectType.map((e) => subjectTypeMap[e.toString()]).join(', ');
                return item;
            })
            .sort((a, b) => (a.title > b.title ? 1 : -1));
    }

    if (props.value.preset === 'EncounterType') {
        let encounterTypes = filteredResources.filter((e) => e.resourceType === 'BN_EncounterType');
        encounterTypes = encounterTypes.filter((encounterType) => {
            if (encounterTypes.find((et) => et.derivedFrom === encounterType.url)) {
                return false;
            }

            return true;
        });
        return encounterTypes;
    }

    if (props.value.preset === 'Service') {
        // add Non-billable service
        filteredResources.push({
            name: 'Non-billable',
            resourceType: 'HealthcareService',
        });
        let result = filteredResources.filter((service) => service.uomType !== 'Diem');
        return result;
    }

    // group special variables
    if (props.value.preset === 'specialVariables') {
        return filteredResources.flatMap((item) => {
            if (item.header) {
                item.icon = 'layer-group';
            }

            return item;
        });
    }

    // no customization just return the resources
    else {
        return filteredResources;
    }
});

// Watchers
watch(
    search,
    debounce(
        () => {
            if (remoteSearch.value) {
                generateQuery(props.value);
            }
        },
        250,
        { maxWait: 500 },
    ),
);

// Methods
async function show(showOptions, showProps) {
    search.value = undefined;
    remoteSearch.value = undefined;
    dpResource.value = [];
    options.value = { ...baseOptions, ...showOptions };

    // set item prop defaults
    showProps.items = showProps.items || [];
    showProps.itemKey = showProps.itemKey || 'id';
    showProps.itemIcon = showProps.itemIcon || ['far', 'circle'];

    // define data query and add additional props as required
    if (!showProps.query) {
        showProps = await generateQuery(showProps);
    }
    // set all other props
    props.value = { ...showProps, selectedResources: [] };

    // show dialog
    dialog.value = true;
    return new Promise((res, rej) => {
        resolve.value = res;
        reject.value = rej;
    });
}

function done() {
    // Check if there's a validation function that can prevent closing
    if (props.value?.onBeforeClose && !props.value.onBeforeClose(props.value?.selectedResources ?? [])) {
        return;
    }

    if (props.value.variation && props.value.selectedResources.length === 1 && props.value.selectedResources[0].transform) {
        addRefToBackBtnListiner(bnDialogRef.value, 'close', 'bnDialog');
        bnDialogRef.value.show({ ...props.value.variation }).then((response) => {
            removeRefToBackBtnListiner('bnDialog');
            if (response) {
                let transform = transformResource(props.value.selectedResources, response);
                if (!options.value.multiple) {
                    transform = transform[0];
                }
                close(transform);
            } else {
                props.value.selectedResources = [];
            }
        });
    } else if (!options.value.multiple) {
        close(props.value.selectedResources[0]);
    } else {
        close(props.value.selectedResources);
    }
}

function close(resolveValue) {
    props.value = {};
    search.value = undefined;
    remoteSearch.value = undefined;
    dpResource.value = [];
    resolve.value(resolveValue);
    dialog.value = false;
    options.value = undefined;
}

async function generateQuery(queryProps) {
    // set agency prop if required
    if (queryProps.agency) {
        if (!Array.isArray(queryProps.agency)) {
            queryProps.agency = [queryProps.agency];
        }
    }
    // set agency if location is specified
    else if (queryProps.location?.id) {
        await $httpFhirApi.get('Location/' + queryProps.location.id).then((response) => {
            queryProps.agency = [response.data?.managingOrganization];
        });
    }
    // default to empty
    else {
        queryProps.agency = [];
    }
    // set agency filter
    agency.value = queryProps.agency[0]?.id;

    // normalize agencies
    queryProps.agency = queryProps.agency.map((item) => ({ id: item.id, display: item.display || item.name, resourceType: 'Organization' }));

    // create agency query string
    queryProps.agencies = queryProps.agency.length ? queryProps.agency.map((item) => item.id).join(',') : undefined;

    // Defined preset query
    if (queryProps.preset) {
        // Form (Questionnaire)
        if (queryProps.preset === 'Questionnaire') {
            queryProps.itemTitle = 'title';
            queryProps.itemSubTitle = 'subTitle';
            queryProps.itemIcon = ['far', 'file-contract'];
            queryProps.itemKey = 'url';
            queryProps.titleChip = true;
            queryProps.subTitleChip = true;
            // queryProps.itemType should be passed in: form, live
            queryProps.itemType = queryProps.itemType || 'form';
            let formStatus = window.$isBnSupport?.() || isProfile() ? 'active,draft' : 'active';

            options.value.group.info = 'Forms are grouped by the contact type they serve. Forms may appear in multiple groups';
            queryProps.query = {
                query: '_elements=title,url,bnOwner,subjectType,name,version,groupNote,status,item.linkId&deprecate:missing=true&status=' + formStatus + '&formType=' + queryProps.itemType,
                resourceType: 'Questionnaire',
            };
            // status defaults to 'active' (published) unless bn support, then include draft
            const status = formStatus || queryProps.status || 'active';
            queryProps.query.query += '&status=' + status;
            // subjectType filters by target contact type
            if (queryProps.subjectType) {
                queryProps.query.query += '&subject-type=' + queryProps.subjectType;
            }
            // infinite scroll / search
            // queryProps.query.query += '&_count=30';

            remoteSearch.value = true;
            if (search.value) {
                queryProps.query.query += '&title:contains=' + search.value;
            }

            if (!queryProps.includeProfileResources) {
                let optionsObj = { query: '&_elements=title,url,status,bnOwner,subjectType,version,name,groupNote,item.linkId' };
                if (queryProps.itemType === 'live') {
                    optionsObj = { query: '&formType=live&_elements=title,url,status,bnOwner,subjectType,version,name,groupNote,item.linkId' };
                }

                const { getProfiledQuestionnaires } = useProfileFlow();
                let formsToQuery = await getProfiledQuestionnaires(optionsObj);

                queryProps.query = [
                    {
                        query: queryProps.query.query,
                        resourceType: 'Questionnaire',
                    },
                ];

                queryProps.query = queryProps.query.concat(formsToQuery);
            }
            const getCollections = {
                query: 'type=formCollection',
                resourceType: 'BN_List',
            };
            queryProps.query = queryProps.query.concat(getCollections);
        }
        // Collections
        else if (queryProps.preset === 'collection') {
            queryProps.itemTitle = 'name';
            queryProps.itemIcon = ['far', 'layer-group'];
            queryProps.itemKey = 'url';
            // queryProps.itemType should be passed in: AutoNoteCollection, FormCollection, LiveFormCollection
            queryProps.itemType = queryProps.itemType || 'undefined';
            queryProps.query = {
                query: '_sort=name&_elements=name,url,owner&type=' + queryProps.itemType,
                resourceType: 'BN_List',
            };
            // filter by agency
            if (queryProps.agencies) {
                queryProps.query.query += '&owner=' + queryProps.agencies;
            }
            // infinite scroll / search
            queryProps.query.query += '&_count=30';
            remoteSearch.value = true;
            if (search.value) {
                queryProps.query.query += '&name:contains=' + search.value;
            }
        }
        // AutoNote
        else if (queryProps.preset === 'AutoNote') {
            queryProps.itemTitle = 'title';
            queryProps.itemIcon = ['far', 'wand-magic-sparkles'];
            queryProps.itemKey = 'url';
            queryProps.query = {
                query: '_sort=title&_elements=title,url,bnOwner&formType=autonote',
                resourceType: 'Questionnaire',
            };
            // filter by agency
            if (queryProps.agencies) {
                queryProps.query.query += '&bnOwner=' + queryProps.agencies;
            }
            // infinite scroll / search
            queryProps.query.query += '&_count=30';
            remoteSearch.value = true;
            if (search.value) {
                queryProps.query.query += '&title:contains=' + search.value;
            }
        }
        // EncounterTypes
        else if (queryProps.preset === 'EncounterType') {
            queryProps.itemTitle = 'title';
            queryProps.itemIcon = ['far', 'tags'];
            queryProps.itemKey = 'url';
            queryProps.query = [
                {
                    query: '_sort=title&active=true',
                    resourceType: 'BN_EncounterType',
                },
            ];

            if (queryProps.encounterTypeLibraryItems) {
                queryProps.encounterTypeLibraryItems.forEach((url) => {
                    queryProps.query.push({ url: url });
                });
            }

            // filter by agency
            if (queryProps.agencies) {
                queryProps.filterByAgency = 'agency';
                queryProps.query.query += '&agency=' + queryProps.agencies;
            }
        }
        // Locations
        else if (queryProps.preset === 'Location') {
            queryProps.query = {
                query: '_sort=name&status=active&partof:missing=true',
                resourceType: 'Location',
            };

            if (queryProps.includeRooms) {
                queryProps.query = {
                    query: '_sort=name&status=active',
                    resourceType: 'Location',
                };
            }

            queryProps.itemTitle = 'name';
            queryProps.itemIcon = ['far', 'map-marker-alt'];
            queryProps.itemKey = 'id';
            // filter by agency
            if (queryProps.agencies) {
                queryProps.filterByAgency = 'managingOrganization';
                queryProps.query.query += '&organization=' + queryProps.agencies;
            }
        }
        // Programs
        else if (queryProps.preset === 'Program') {
            queryProps.query = {
                query: 'isProgram=true&isAddOn:missing=true&active=true&_sort=name',
                resourceType: 'HealthcareService',
            };
            queryProps.itemTitle = 'name';
            queryProps.itemIcon = ['far', 'route'];
            queryProps.itemKey = 'id';
            // filter by agency
            if (queryProps.agencies) {
                queryProps.filterByAgency = 'providedBy';
                queryProps.query.query += '&organization=' + queryProps.agencies;
            }
        }
        // Services
        else if (queryProps.preset === 'Service') {
            queryProps.query = {
                query: 'isProgram:missing=true&isAddOn:missing=true&active=true&_sort=name',
                resourceType: 'HealthcareService',
            };
            queryProps.itemTitle = 'name';
            queryProps.itemIcon = ['far', 'clipboard-list-check'];
            queryProps.itemKey = 'id';
        }

        // Place of Service
        else if (queryProps.preset === 'PlaceOfService') {
            queryProps.query = {
                query: 'system=http://code.system/cms-place-of-service&_sort=code',
                resourceType: 'Concept',
                terminology: true,
            };
            queryProps.itemTitle = ['code', 'display'];
            queryProps.itemIcon = ['far', 'map-marker-alt'];
            queryProps.itemKey = 'code';
        }

        // Special variables - comes from a CONSTANT - SPECIAL_VARIABLES
        else if (queryProps.preset === 'specialVariables') {
            queryProps.query = {};
            // Done so that the data provider is not used because the call to
            // CreateFHIRResource was causing and error because of the recordType
            // property (Attempt to create a Patient when it is really just a JSON object).
            dpResource.value = specialVariables;
            queryProps.itemTitle = 'label';
            queryProps.itemSubTitle = 'token';
            queryProps.itemIcon = ['far', 'brackets-curly'];
            queryProps.itemKey = 'token';
            queryProps.variation = {
                type: 'radio',
                options: [
                    { text: 'Uppercase', value: 'upper' },
                    { text: 'Lowercase', value: 'lower' },
                ],
                value: 'upper',
                width: 300,
                header: 'Capitalization',
                message: 'First letter should be:',
                confirm: 'Insert',
                cancel: 'Cancel',
            };
        }
    }
    // ResourceType
    else if (queryProps.resourceType) {
        queryProps.query = {
            resourceType: queryProps.resourceType,
        };
    }
    return queryProps;
}

// scroll detection to load more resources
function loadMoreData(event) {
    if (!moreDataUrl.value) return;
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollTop + clientHeight >= scrollHeight - 5 && moreDataUrl.value && !loadingMoreData.value) {
        loadingMoreData.value = true;
        dpResourceRef.value.fetchItems({ url: moreDataUrl.value });
    }
}

function selectResource(resource) {
    const alreadySelected = props.value.selectedResources.findIndex((item) => item[props.value.itemKey] === resource[props.value.itemKey]);
    if (alreadySelected > -1) {
        props.value.selectedResources.splice(alreadySelected, 1);
    } else {
        props.value.selectedResources.push(resource);
    }
    if (!options.value.multiple) done();
}

function transformResource(resource, variation) {
    const transformedResource = JSON.parse(JSON.stringify(resource));
    if (props.value.preset === 'specialVariables') {
        transformedResource[0].case = variation;
        if (variation === 'lower') {
            transformedResource[0].token = transformedResource[0].token.replace('{X', '{x');
        } else {
            transformedResource[0].token = transformedResource[0].token.replace('{x', '{X');
        }
    }
    return transformedResource;
}

function getItemTitle(item, separator = ' - ') {
    // simple string
    if (typeof item === 'string') {
        return { text: item };
    }

    // If the item being passed is a HealthcareService return the request format
    if (item instanceof HealthcareService && item.billingCode) {
        return {
            text: '(' + item.billingCode + ')' + (item.description ? ' - ' + item.description : ''),
        };
    }

    // all other types
    if (typeof props.value.itemTitle === 'object' && !Array.isArray(props.value.itemTitle)) {
        const title = item[props.value.itemTitle.title];
        return typeof title === 'object' ? title : { text: title };
    } else if (props.value.itemTitle) {
        if (props.value.itemTitle === 'url') {
            return { text: getDisplay(item?.url) };
        }
        if (Array.isArray(props.value.itemTitle) && props.value.itemTitle.length >= 2) {
            let stringFromArray = '';
            props.value.itemTitle.forEach((e, index) => {
                if (item[e.toString()]) {
                    stringFromArray += item[e.toString()];
                    if (index !== props.value.itemTitle.length - 1) {
                        stringFromArray += separator;
                    }
                }
            });
            return { text: stringFromArray };
        }
        return { text: item[props.value.itemTitle] };
    } else {
        return { text: item?.display || item?.text || item?.name };
    }
}

function getItemSubTitle(item) {
    const subtitle = item[props.value.itemTitle?.subtitle];
    return typeof subtitle === 'object' ? subtitle : { text: subtitle };
}

function isManagedResource(resource) {
    if (['Questionnaire', 'BN_EncounterType'].includes(resource.resourceType)) {
        return !isLibraryDatabase() && (resource.url?.includes('bnprofile.fhir') || resource.derivedFrom?.includes('bnprofile.fhir'));
    } else if (resource.resourceType === 'HealthcareService') {
        return !!resource.derivedFrom;
    }

    return false;
}

function isFormAdded(url) {
    // Compare the base URL to check if the form is already added
    return props.value.items.some((item) => {
        return item.url && typeof item.url === 'string' && item.url.split('|')[0] === url;
    });
}

function hasSelectedResource(resource) {
    return props.value.items.some((item) => {
        // Check if either object is a non-billable service using their specific structures
        const isResourceNonBillable = resource.display === 'non-billable' || resource.name === 'Non-billable';
        const isItemNonBillable = item.code?.coding?.[0]?.version === 'non-billable';

        if (isResourceNonBillable || isItemNonBillable) {
            return isResourceNonBillable && isItemNonBillable;
        }

        if (item.resourceType === 'Procedure') {
            return resource[props.value.itemKey] === item?.code?.coding?.[0]?.version;
        }
        return item[props.value.itemKey] === resource[props.value.itemKey];
    });
}

function onLoaded() {
    loadingMoreData.value = false;
}

// Expose methods for parent components
defineExpose({
    show,
    close,
    done,
});
</script>

<template>
    <v-dialog v-if="options" v-model="dialog" :width="options.width" scrollable :persistent="options.persistent" :fullscreen="display.xs.value">
        <v-card flat v-bind="$attrs" class="d-flex flex-column">
            <div class="flex-grow-0">
                <ion-header>
                    <ion-toolbar>
                        <v-toolbar density="compact">
                            <v-toolbar-title>{{ options.header }}</v-toolbar-title>
                            <template v-if="options.multiple">
                                <BnToolbarBtn type="text" :icon="options.cancelIcon || 'times'" :label="options.cancel || 'Cancel'" data-cy="resourceSelectorCloseButton" @click="close" />
                                <BnToolbarBtn
                                    type="outlined"
                                    right
                                    color="primary"
                                    :icon="options.confirmIcon || 'check'"
                                    :label="options.confirm || 'Done'"
                                    :disabled="props.selectedResources && !props.selectedResources.length"
                                    data-cy="resourceSelectorDoneButton"
                                    @click="done"
                                />
                            </template>
                            <BnToolbarBtn v-else type="outlined" right color="primary" icon="times" label="Cancel" data-cy="resourceSelectorCloseButton" @click="close" />
                        </v-toolbar>
                    </ion-toolbar>
                </ion-header>
                <v-divider></v-divider>
                <!-- Custom content slot (currently used for location selector when starting ad hoc group encounters from Contacts tab) -->
                <slot name="prepend"></slot>
                <!-- Search/Filter Bar -->
                <template v-if="options.filter">
                    <div class="d-flex bn-color-list-filter">
                        <v-text-field ref="searchRef" v-model="search" autofocus placeholder="Search" clearable hide-details background-color="bn-color-list-filter" data-cy="resourceSelectorSearch">
                            <template #prepend-inner>
                                <font-awesome-icon :icon="['far', 'search']" class="align-self-center mr-2" />
                            </template>
                        </v-text-field>
                    </div>
                    <v-divider></v-divider>

                    <!-- Display message when no live forms are found, only for forms -->
                    <div v-if="props.preset === 'Questionnaire' && resources.length === 0 && props.itemType === 'live'" class="text-center text-caption pa-3">No live forms setup for your use</div>
                </template>
                <!-- Agency Selector (if more than 1) -->
                <template v-if="props.filterByAgency && props.agency.length > 1">
                    <div class="d-flex bn-color-list-filter no-select">
                        <v-select
                            v-model="agency"
                            :items="props.agency"
                            item-value="id"
                            item-label="display"
                            hide-details
                            background-color="bn-color-list-filter"
                            @update:model-value="options.filter ? searchRef.focus() : undefined"
                        >
                            <template #prepend-inner>
                                <font-awesome-icon :icon="['far', 'building']" class="align-self-center mr-2" />
                            </template>
                        </v-select>
                    </div>
                    <v-divider></v-divider>
                </template>
                <!-- Info/Warning Bars -->
                <div v-if="options.info || options.warning" class="no-select mb-4">
                    <BnInfo v-if="options.info" type="outlined">
                        {{ options.info }}
                    </BnInfo>
                    <BnInfo v-if="options.warning" type="outlined" color="warning">
                        {{ options.warning }}
                    </BnInfo>
                </div>
            </div>
            <div class="scroll-on no-select" :min-height="display.mdAndUp.value ? 400 : undefined" :max-height="display.mdAndUp.value ? 800 : undefined" @scroll="loadMoreData">
                <DataProvider
                    v-if="!isEmpty(props.query) || !isEmpty(props.resource)"
                    ref="dpResourceRef"
                    v-model="dpResource"
                    collection
                    refresh-on-change
                    :resource="props.resource"
                    :query="props.query"
                    @loaded="onLoaded"
                    @loaded-items="onLoaded"
                >
                </DataProvider>

                <BnLoading v-if="loadingMoreData" card class="pa-4" />

                <v-list v-else class="pa-0" data-cy="resourceSelectorList">
                    <template v-for="(resource, index) in resources" :key="index">
                        <!-- GroupBy header -->
                        <template v-if="resource.header">
                            <v-divider v-if="index"></v-divider>
                            <v-list-item class="bn-color-list-group font-weight-medium pr-0">
                                <template v-if="resource.icon" #prepend>
                                    <font-awesome-icon v-if="resource.icon" class="mr-2" :icon="['far', resource.icon]" />
                                </template>

                                <v-list-item-title class="bn-section-header">{{ resource.header }}</v-list-item-title>
                            </v-list-item>
                        </template>
                        <template v-else>
                            <v-divider v-if="index"></v-divider>
                            <v-list-item
                                color="bn-color-list-item"
                                class="pr-0"
                                :disabled="hasSelectedResource(resource) || isFormAdded(resource.url)"
                                :active="props.selectedResources.some((item) => item[props.itemKey] === resource[props.itemKey])"
                                data-cy="resourceSelectorListItem"
                                @click="selectResource(resource)"
                            >
                                <template #prepend="{ isActive }">
                                    <v-icon v-if="typeof resource.icon === 'string' && resource.icon.startsWith('$')" :icon="resource.icon" class="mr-2" :class="{ 'text-black': !isActive }"></v-icon>
                                    <i
                                        v-else-if="typeof resource.icon === 'string' && resource.icon.startsWith('fa-kit')"
                                        class="v-icon v-icon--size-default mr-2"
                                        :class="resource.icon + (!isActive ? ' text-black' : '')"
                                    ></i>
                                    <font-awesome-icon v-else-if="resource.icon" :class="{ 'text-black': !isActive }" class="v-icon mr-2" :icon="resource.icon" size="lg" />
                                    <font-awesome-icon v-else class="v-icon mr-2" :class="{ 'text-black': !isActive }" :icon="props.itemIcon" size="lg" />
                                </template>

                                <div>
                                    <v-list-item-title data-cy="resourceItemTitle">
                                        {{ getItemTitle(resource).text }}
                                        <template v-if="props.titleChip && resource.name">
                                            <v-chip size="x-small" label class="mr-2">{{ resource.name }}</v-chip>
                                        </template>
                                    </v-list-item-title>
                                    <v-list-item-subtitle v-if="getItemSubTitle(resource).text">{{ resource[props.itemSubTitle] }}</v-list-item-subtitle>
                                    <v-list-item-subtitle v-if="props.subTitleChip">
                                        <v-chip color="success" variant="outlined" size="x-small" class="flex-grow-0">
                                            <span v-if="resource.version">Version {{ resource.version }}</span>
                                        </v-chip>
                                        <v-chip v-show="resource.collectionName" class="ml-1" variant="outlined" size="x-small">
                                            <font-awesome-icon :icon="['far', 'layer-group']" class="mr-1"></font-awesome-icon>{{ resource.collectionName }}
                                        </v-chip>
                                        <v-chip v-if="props.preset === 'Questionnaire' && resource.status === 'draft'" class="ml-1" color="warning" size="x-small">
                                            <span>Draft</span>
                                        </v-chip>
                                    </v-list-item-subtitle>
                                    <v-spacer></v-spacer>
                                </div>
                                <template #append="{ isActive }">
                                    <div v-if="isManagedResource(resource)">
                                        <BnInfo :header="'Managed ' + resource.resourceType" icon="file-certificate" type="dialog"> This item is based on a BestNotes Managed item. </BnInfo>
                                    </div>
                                    <!-- Non-outlined chip replacing the checkmark icon -->
                                    <v-chip v-if="isFormAdded(resource.url)" size="small" class="mx-2"> Added </v-chip>
                                    <div v-else-if="!props.items.some((item) => [item[props.itemKey]].includes(resource[props.itemKey]))" class="ma-3" :class="{ 'text-grey-lighten-2': !isActive }">
                                        <font-awesome-icon :icon="['far', 'check']" size="lg" />
                                    </div>
                                    <v-chip v-else size="small" class="mx-2">added</v-chip>
                                </template>
                            </v-list-item>
                        </template>
                    </template>
                </v-list>

                <bn-dialog ref="bnDialogRef"></bn-dialog>
            </div>
            <!-- ios ample touch footer bar -->
            <BnFooter class="dialog-footer"></BnFooter>
        </v-card>
    </v-dialog>
</template>
